import React, { Fragment, useMemo } from "react";

import { identity, orderBy } from "lodash";
import { Link } from "react-router-dom";

import useClientDashboard from "@fartherfinance/frontend/api/Dashboard/hooks/useClientDashboard";
import useClientProfile from "@fartherfinance/frontend/api/Entity/hooks/useClientProfile";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import useTaxBudgets from "@src/multiCustodian/components/TaxBudgets/Hooks/useTaxBudgets";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import Card from "@src/sharedComponents/Card/Card";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./ProposalDetails.module.css";

interface Props {
  clientIds: ClientId[] | undefined; // undefined means loading
}

const CardUser: React.FC<Props> = (props) => {
  const orderedClients = useMemo(() => {
    if (props.clientIds === undefined) {
      return undefined;
    }

    return orderBy(props.clientIds, [identity], ["asc"]);
  }, [props.clientIds]);

  return (
    <Card header="Farther User">
      {(orderedClients ?? [undefined]).map((clientId, idx) => (
        <Fragment key={`${clientId ?? ""}-${idx}`}>
          {idx > 0 && <div className={styles.Divider} />}
          <SingleClientSection clientId={clientId} />
        </Fragment>
      ))}

      <div className={styles.VerticalSeparator} />

      <div className={styles.FlexRow}>
        <Link to="#">
          <Text variant="body">Trading Group</Text>
        </Link>
      </div>
    </Card>
  );
};

export default CardUser;

interface SingleClientSectionProps {
  clientId: ClientId | undefined;
}

const SingleClientSection: React.FC<SingleClientSectionProps> = (props) => {
  const auth = useAdvisorRequestAuth();

  const clientAuth = useMemo(
    () =>
      auth === null || props.clientId === undefined
        ? null
        : { ...auth, clientId: props.clientId },
    [auth, props.clientId]
  );
  const clientProfile = useClientProfile(props.clientId ?? null, clientAuth);

  const dashboard = useClientDashboard(props.clientId ?? null, clientAuth);

  const taxBudget = useTaxBudgets(
    props.clientId === undefined ? [] : [props.clientId],
    auth ?? clientAuth
  );

  const fullName =
    clientProfile.data === undefined
      ? undefined
      : `${clientProfile.data.investorProfile.personalDetails.name.first} ${clientProfile.data.investorProfile.personalDetails.name.last}`;

  const advisorFullName =
    dashboard.data === undefined
      ? undefined
      : `${dashboard.data.advisor.name.first} ${dashboard.data.advisor.name.last}`;

  const hasTaxBudget =
    taxBudget.data === undefined
      ? undefined
      : taxBudget.data.currentBudget !== undefined;

  return (
    <>
      <div className={styles.FlexRow}>
        <Text variant="bodyBold">User</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">{fullName ?? <Skeleton width={100} />}</Text>
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">ID</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">{props.clientId ?? <Skeleton width={100} />}</Text>
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Advisor</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {advisorFullName ?? <Skeleton width={100} />}
        </Text>
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Tax Budget</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {hasTaxBudget === undefined ? (
            <Skeleton width={100} />
          ) : hasTaxBudget ? (
            "Yes"
          ) : (
            "No"
          )}
        </Text>
      </div>
    </>
  );
};
